import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  Tabs,
  Tab,
  Badge,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import moment from 'moment';

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [verifySuccess, setVerifySuccess] = useState(false);
  const [rejectSuccess, setRejectSuccess] = useState(false);
  
  // 模擬加載數據
  useEffect(() => {
    // 這裡將是API調用
    setTimeout(() => {
      const mockPayments = [
        {
          id: 1,
          unitId: 'A-1201',
          residentName: '陳大文',
          type: 'management_fee',
          amount: 1500,
          date: '2023-06-15',
          reference: 'MF-202306-1201',
          status: 'pending',
          fileName: 'management_fee_jun2023.pdf',
          fileType: 'application/pdf',
          uploadDate: '2023-06-16'
        },
        {
          id: 2,
          unitId: 'A-1202',
          residentName: '李小明',
          type: 'water_bill',
          amount: 320,
          date: '2023-06-18',
          reference: 'WB-202306-1202',
          status: 'pending',
          fileName: 'water_bill_jun2023.jpg',
          fileType: 'image/jpeg',
          uploadDate: '2023-06-19'
        },
        {
          id: 3,
          unitId: 'B-0802',
          residentName: '黃麗華',
          type: 'electricity_bill',
          amount: 780,
          date: '2023-06-14',
          reference: 'EB-202306-0802',
          status: 'verified',
          verifiedBy: '管理員張',
          verifiedDate: '2023-06-15',
          fileName: 'electricity_bill_jun2023.pdf',
          fileType: 'application/pdf',
          uploadDate: '2023-06-15'
        },
        {
          id: 4,
          unitId: 'B-1503',
          residentName: '王建國',
          type: 'gas_bill',
          amount: 450,
          date: '2023-06-10',
          reference: 'GB-202306-1503',
          status: 'rejected',
          rejectedBy: '管理員李',
          rejectedDate: '2023-06-12',
          rejectionReason: '收據不清晰，請重新上傳',
          fileName: 'gas_bill_jun2023.png',
          fileType: 'image/png',
          uploadDate: '2023-06-11'
        },
        {
          id: 5,
          unitId: 'C-0505',
          residentName: '林美玲',
          type: 'management_fee',
          amount: 1500,
          date: '2023-06-05',
          reference: 'MF-202306-0505',
          status: 'pending',
          fileName: 'management_fee_jun2023.jpg',
          fileType: 'image/jpeg',
          uploadDate: '2023-06-06'
        }
      ];
      
      setPayments(mockPayments);
      setFilteredPayments(mockPayments);
      setLoading(false);
    }, 1000);
  }, []);
  
  // 處理搜索和過濾
  useEffect(() => {
    let result = payments;
    
    // 搜索詞過濾
    if (searchTerm) {
      result = result.filter(payment => 
        payment.unitId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.residentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.reference.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 付款類型過濾
    if (filterType !== 'all') {
      result = result.filter(payment => payment.type === filterType);
    }
    
    // 狀態過濾
    if (filterStatus !== 'all') {
      result = result.filter(payment => payment.status === filterStatus);
    }
    
    setFilteredPayments(result);
  }, [searchTerm, filterType, filterStatus, payments]);
  
  // 打開詳情對話框
  const handleOpenDialog = (payment) => {
    setSelectedPayment(payment);
    setDialogOpen(true);
  };
  
  // 關閉詳情對話框
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // 打開確認對話框
  const handleOpenVerifyDialog = (e, payment) => {
    e.stopPropagation();
    setSelectedPayment(payment);
    setVerifyDialogOpen(true);
  };
  
  // 關閉確認對話框
  const handleCloseVerifyDialog = () => {
    setVerifyDialogOpen(false);
  };
  
  // 打開拒絕對話框
  const handleOpenRejectDialog = (e, payment) => {
    e.stopPropagation();
    setSelectedPayment(payment);
    setRejectDialogOpen(true);
  };
  
  // 關閉拒絕對話框
  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectReason('');
  };
  
  // 確認付款
  const handleVerifyPayment = () => {
    // 這裡將是API調用
    const updatedPayments = payments.map(payment => {
      if (payment.id === selectedPayment.id) {
        return {
          ...payment,
          status: 'verified',
          verifiedBy: '當前管理員',
          verifiedDate: moment().format('YYYY-MM-DD')
        };
      }
      return payment;
    });
    
    setPayments(updatedPayments);
    setVerifyDialogOpen(false);
    setVerifySuccess(true);
    
    // 3秒後隱藏通知
    setTimeout(() => {
      setVerifySuccess(false);
    }, 3000);
  };
  
  // 拒絕付款
  const handleRejectPayment = () => {
    if (!rejectReason.trim()) return;
    
    // 這裡將是API調用
    const updatedPayments = payments.map(payment => {
      if (payment.id === selectedPayment.id) {
        return {
          ...payment,
          status: 'rejected',
          rejectedBy: '當前管理員',
          rejectedDate: moment().format('YYYY-MM-DD'),
          rejectionReason: rejectReason
        };
      }
      return payment;
    });
    
    setPayments(updatedPayments);
    setRejectDialogOpen(false);
    setRejectSuccess(true);
    setRejectReason('');
    
    // 3秒後隱藏通知
    setTimeout(() => {
      setRejectSuccess(false);
    }, 3000);
  };
  
  // 格式化付款類型
  const formatPaymentType = (type) => {
    switch (type) {
      case 'management_fee':
        return '管理費';
      case 'water_bill':
        return '水費';
      case 'electricity_bill':
        return '電費';
      case 'gas_bill':
        return '煤氣費';
      case 'rental':
        return '租金';
      default:
        return type;
    }
  };
  
  // 獲取狀態標籤
  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip size="small" label="待審核" color="primary" />;
      case 'verified':
        return <Chip size="small" label="已確認" color="success" icon={<CheckCircleIcon />} />;
      case 'rejected':
        return <Chip size="small" label="已拒絕" color="error" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>付款管理</Typography>
      
      {verifySuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          付款已成功確認！
        </Alert>
      )}
      
      {rejectSuccess && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          付款已被拒絕！
        </Alert>
      )}
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="搜索單位/住戶/參考編號"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>付款類型</InputLabel>
              <Select
                value={filterType}
                label="付款類型"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">所有類型</MenuItem>
                <MenuItem value="management_fee">管理費</MenuItem>
                <MenuItem value="water_bill">水費</MenuItem>
                <MenuItem value="electricity_bill">電費</MenuItem>
                <MenuItem value="gas_bill">煤氣費</MenuItem>
                <MenuItem value="rental">租金</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>狀態</InputLabel>
              <Select
                value={filterStatus}
                label="狀態"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">所有狀態</MenuItem>
                <MenuItem value="pending">待審核</MenuItem>
                <MenuItem value="verified">已確認</MenuItem>
                <MenuItem value="rejected">已拒絕</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button 
              variant="outlined" 
              fullWidth
              startIcon={<FilterListIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
                setFilterStatus('all');
              }}
            >
              重置
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>載入中...</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>單位</TableCell>
                <TableCell>住戶</TableCell>
                <TableCell>類型</TableCell>
                <TableCell>金額</TableCell>
                <TableCell>日期</TableCell>
                <TableCell>參考編號</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => (
                  <TableRow 
                    key={payment.id}
                    hover
                    onClick={() => handleOpenDialog(payment)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{payment.unitId}</TableCell>
                    <TableCell>{payment.residentName}</TableCell>
                    <TableCell>{formatPaymentType(payment.type)}</TableCell>
                    <TableCell>${payment.amount.toFixed(2)}</TableCell>
                    <TableCell>{moment(payment.date).format('YYYY/MM/DD')}</TableCell>
                    <TableCell>{payment.reference}</TableCell>
                    <TableCell>{getStatusChip(payment.status)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton 
                          size="small" 
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(payment);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        
                        {payment.status === 'pending' && (
                          <>
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={(e) => handleOpenVerifyDialog(e, payment)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={(e) => handleOpenRejectDialog(e, payment)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    沒有找到付款記錄
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* 付款詳情對話框 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {selectedPayment && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                付款詳情 - {selectedPayment.unitId}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    基本資訊
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          單位
                        </Typography>
                        <Typography variant="body1">
                          {selectedPayment.unitId}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          住戶
                        </Typography>
                        <Typography variant="body1">
                          {selectedPayment.residentName}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          付款類型
                        </Typography>
                        <Typography variant="body1">
                          {formatPaymentType(selectedPayment.type)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          金額
                        </Typography>
                        <Typography variant="body1">
                          ${selectedPayment.amount.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          付款日期
                        </Typography>
                        <Typography variant="body1">
                          {moment(selectedPayment.date).format('YYYY/MM/DD')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          上傳日期
                        </Typography>
                        <Typography variant="body1">
                          {moment(selectedPayment.uploadDate).format('YYYY/MM/DD')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          參考編號
                        </Typography>
                        <Typography variant="body1">
                          {selectedPayment.reference}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ mt: 1 }}>
                          {getStatusChip(selectedPayment.status)}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  {selectedPayment.status === 'verified' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        確認資訊
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              確認者
                            </Typography>
                            <Typography variant="body1">
                              {selectedPayment.verifiedBy}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              確認日期
                            </Typography>
                            <Typography variant="body1">
                              {moment(selectedPayment.verifiedDate).format('YYYY/MM/DD')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )}
                  
                  {selectedPayment.status === 'rejected' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        拒絕資訊
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              拒絕者
                            </Typography>
                            <Typography variant="body1">
                              {selectedPayment.rejectedBy}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              拒絕日期
                            </Typography>
                            <Typography variant="body1">
                              {moment(selectedPayment.rejectedDate).format('YYYY/MM/DD')}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              拒絕原因
                            </Typography>
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {selectedPayment.rejectionReason}
                            </Alert>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    收據文件
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      {selectedPayment.fileType.startsWith('image/') ? (
                        <ImageIcon color="primary" sx={{ fontSize: 64 }} />
                      ) : (
                        <PictureAsPdfIcon color="error" sx={{ fontSize: 64 }} />
                      )}
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedPayment.fileName}
                      </Typography>
                    </Box>
                    
                    <Button variant="contained">
                      查看收據
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                {selectedPayment.status === 'pending' && (
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => {
                        handleCloseDialog();
                        handleOpenVerifyDialog(new Event('click'), selectedPayment);
                      }}
                    >
                      確認付款
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        handleCloseDialog();
                        handleOpenRejectDialog(new Event('click'), selectedPayment);
                      }}
                    >
                      拒絕付款
                    </Button>
                  </Stack>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>關閉</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* 確認付款對話框 */}
      <Dialog open={verifyDialogOpen} onClose={handleCloseVerifyDialog} maxWidth="xs" fullWidth>
        {selectedPayment && (
          <>
            <DialogTitle>確認付款</DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph>
                您確定要確認以下付款嗎？
              </Typography>
              <Typography variant="body2">
                單位: {selectedPayment.unitId}
              </Typography>
              <Typography variant="body2">
                住戶: {selectedPayment.residentName}
              </Typography>
              <Typography variant="body2">
                類型: {formatPaymentType(selectedPayment.type)}
              </Typography>
              <Typography variant="body2">
                金額: ${selectedPayment.amount.toFixed(2)}
              </Typography>
              <Typography variant="body2">
                參考編號: {selectedPayment.reference}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseVerifyDialog}>取消</Button>
              <Button variant="contained" color="success" onClick={handleVerifyPayment}>
                確認付款
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* 拒絕付款對話框 */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="xs" fullWidth>
        {selectedPayment && (
          <>
            <DialogTitle>拒絕付款</DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph>
                請提供拒絕此付款的原因：
              </Typography>
              <TextField
                fullWidth
                label="拒絕原因"
                multiline
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                margin="normal"
                required
                error={rejectReason.trim() === ''}
                helperText={rejectReason.trim() === '' ? '請輸入拒絕原因' : ''}
              />
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  單位: {selectedPayment.unitId}
                </Typography>
                <Typography variant="body2">
                  住戶: {selectedPayment.residentName}
                </Typography>
                <Typography variant="body2">
                  類型: {formatPaymentType(selectedPayment.type)}
                </Typography>
                <Typography variant="body2">
                  金額: ${selectedPayment.amount.toFixed(2)}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRejectDialog}>取消</Button>
              <Button 
                variant="contained" 
                color="error" 
                onClick={handleRejectPayment}
                disabled={rejectReason.trim() === ''}
              >
                拒絕付款
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PaymentManagement; 